import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Send, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';

type Tenant = { id: string; name: string };

type LineItem = {
  productName: string;
  productCode: string;
  quantity: number;
  unitCode: string;
  isHazardous: boolean;
  unNumber: string;
  hazardClass: string;
  notes: string;
};

const emptyLine = (): LineItem => ({
  productName: '',
  productCode: '',
  quantity: 1,
  unitCode: 'H87',
  isHazardous: false,
  unNumber: '',
  hazardClass: '',
  notes: '',
});

const units = [
  { value: 'H87', label: 'H87 (kom)' },
  { value: 'KGM', label: 'KGM (kg)' },
  { value: 'LTR', label: 'LTR (l)' },
  { value: 'MTR', label: 'MTR (m)' },
  { value: 'MTQ', label: 'MTQ (m³)' },
];

export function NewDespatchAdvicePage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [issues, setIssues] = useState<{ field: string; message: string }[] | null>(null);

  // Header
  const [documentNumber, setDocumentNumber] = useState('');
  const [despatchTypeCode, setDespatchTypeCode] = useState<'Int' | 'Ext'>('Int');
  const [shipmentMethod, setShipmentMethod] = useState('ROAD');
  const today = new Date().toISOString().slice(0, 10);

  // Dates
  const [plannedDespatchDate, setPlannedDespatchDate] = useState(today);
  const [plannedDeliveryEndDate, setPlannedDeliveryEndDate] = useState(today);
  const [actualDespatchDate, setActualDespatchDate] = useState(today);

  // Shipment
  const [grossWeight, setGrossWeight] = useState('');
  const [grossWeightUnit, setGrossWeightUnit] = useState<'GRM' | 'KGM' | 'TNE'>('KGM');
  const [grossVolume, setGrossVolume] = useState('');
  const [grossVolumeUnit, setGrossVolumeUnit] = useState<'MTQ' | 'LTR'>('MTQ');
  const [packageCount, setPackageCount] = useState('');

  // Customer
  const [customerTaxId, setCustomerTaxId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerJbkjs, setCustomerJbkjs] = useState('');

  // Carrier (required for Ext)
  const [carrierTaxId, setCarrierTaxId] = useState('');
  const [carrierName, setCarrierName] = useState('');

  // Lines
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);

  useEffect(() => {
    api.get<Tenant[]>('/admin/tenants').then((rows) => {
      setTenants(rows);
      if (rows.length > 0) setSelectedTenantId(rows[0].id);
    }).catch(() => {});
  }, []);

  const updateLine = (idx: number, key: keyof LineItem, value: any) => {
    setLines((prev) => prev.map((l, i) => i === idx ? { ...l, [key]: value } : l));
  };

  const removeLine = (idx: number) => {
    setLines((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  const buildPayload = () => ({
    tenantId: selectedTenantId,
    documentNumber: documentNumber.trim(),
    despatchTypeCode,
    shipmentMethod,
    issueDate: today,
    plannedDespatchDate,
    plannedDeliveryEndDate,
    actualDespatchDate,
    grossWeight: grossWeight ? { value: Number(grossWeight), unit: grossWeightUnit } : undefined,
    grossVolume: grossVolume ? { value: Number(grossVolume), unit: grossVolumeUnit } : undefined,
    packageCount: packageCount ? Number(packageCount) : undefined,
    customer: {
      name: customerName,
      taxId: customerTaxId,
      jbkjs: customerJbkjs || undefined,
    },
    carrier: despatchTypeCode === 'Ext' && carrierTaxId ? {
      name: carrierName,
      taxId: carrierTaxId,
    } : undefined,
    lines: lines.map((l) => ({
      productName: l.productName,
      productCode: l.productCode || undefined,
      quantity: Number(l.quantity),
      unitCode: l.unitCode,
      isHazardous: l.isHazardous,
      unNumber: l.isHazardous ? l.unNumber : undefined,
      hazardClass: l.isHazardous ? l.hazardClass : undefined,
      notes: l.notes || undefined,
    })),
  });

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    setIssues(null);
    try {
      const result = await api.post<{ id: string }>('/admin/eotpremnica/despatch-advices', buildPayload());
      navigate(`/eotpremnica/documents/${result.id}`);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška pri čuvanju');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSubmit = async () => {
    setSaving(true);
    setError(null);
    setIssues(null);
    try {
      const result = await api.post<{ id: string }>('/admin/eotpremnica/despatch-advices', buildPayload());
      try {
        await api.post(`/admin/eotpremnica/documents/${result.id}/submit`, {});
        navigate(`/eotpremnica/documents/${result.id}`);
      } catch (submitErr: any) {
        if (submitErr?.data?.issues) {
          setIssues(submitErr.data.issues);
          setError(submitErr.data.message || 'Validacija pala');
        } else {
          setError(submitErr?.data?.message || submitErr?.message || 'Slanje palo');
          navigate(`/eotpremnica/documents/${result.id}`);
        }
      }
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <button onClick={() => navigate('/eotpremnica')} style={iconBtn}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>Nova eOtpremnica</h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            UBL Despatch Advice za Centralni Registar
          </div>
        </div>
      </header>

      {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
      {issues && issues.length > 0 && (
        <div style={{ ...errorBox, display: 'block' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={14} /> Greške validacije:
          </div>
          <ul style={{ margin: 0, paddingLeft: 22, fontSize: 12 }}>
            {issues.map((iss, i) => <li key={i}><strong>{iss.field}:</strong> {iss.message}</li>)}
          </ul>
        </div>
      )}

      <Section title="Osnovni podaci">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Tenant">
            <select value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)} style={selectStyle}>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </Field>
          <Field label="Broj otpremnice">
            <input type="text" value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} style={inputStyle} placeholder="OTP-2026-0001" />
          </Field>
          <Field label="Tip">
            <select value={despatchTypeCode} onChange={(e) => setDespatchTypeCode(e.target.value as 'Int' | 'Ext')} style={selectStyle}>
              <option value="Int">Int (interna)</option>
              <option value="Ext">Ext (eksterna)</option>
            </select>
          </Field>
        </div>
        <Field label="Način otpreme (shipment method kod)">
          <input type="text" value={shipmentMethod} onChange={(e) => setShipmentMethod(e.target.value)} style={inputStyle} placeholder="npr. ROAD" />
        </Field>
      </Section>

      <Section title="Datumi">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <Field label="Planirani početak otpreme">
            <input type="date" value={plannedDespatchDate} onChange={(e) => setPlannedDespatchDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Planirani završetak otpreme">
            <input type="date" value={plannedDeliveryEndDate} onChange={(e) => setPlannedDeliveryEndDate(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="Stvarni datum otpreme">
            <input type="date" value={actualDespatchDate} onChange={(e) => setActualDespatchDate(e.target.value)} style={inputStyle} />
          </Field>
        </div>
      </Section>

      <Section title="Primalac">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 14 }}>
          <Field label="PIB"><input type="text" value={customerTaxId} onChange={(e) => setCustomerTaxId(e.target.value)} style={inputStyle} placeholder="9 cifara" /></Field>
          <Field label="Naziv"><input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} style={inputStyle} /></Field>
          <Field label="JBKJS (budžet)"><input type="text" value={customerJbkjs} onChange={(e) => setCustomerJbkjs(e.target.value)} style={inputStyle} /></Field>
        </div>
      </Section>

      {despatchTypeCode === 'Ext' && (
        <Section title="Prevoznik (obavezan za Ext)">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14 }}>
            <Field label="PIB"><input type="text" value={carrierTaxId} onChange={(e) => setCarrierTaxId(e.target.value)} style={inputStyle} placeholder="9 cifara" /></Field>
            <Field label="Naziv"><input type="text" value={carrierName} onChange={(e) => setCarrierName(e.target.value)} style={inputStyle} /></Field>
          </div>
        </Section>
      )}

      <Section title="Podaci o pošiljci (opciono)">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 1fr 100px 1fr', gap: 14, alignItems: 'end' }}>
          <Field label="Bruto težina">
            <input type="number" step="any" value={grossWeight} onChange={(e) => setGrossWeight(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="JM">
            <select value={grossWeightUnit} onChange={(e) => setGrossWeightUnit(e.target.value as any)} style={selectStyle}>
              <option value="GRM">GRM</option>
              <option value="KGM">KGM</option>
              <option value="TNE">TNE</option>
            </select>
          </Field>
          <Field label="Bruto zapremina">
            <input type="number" step="any" value={grossVolume} onChange={(e) => setGrossVolume(e.target.value)} style={inputStyle} />
          </Field>
          <Field label="JM">
            <select value={grossVolumeUnit} onChange={(e) => setGrossVolumeUnit(e.target.value as any)} style={selectStyle}>
              <option value="MTQ">MTQ</option>
              <option value="LTR">LTR</option>
            </select>
          </Field>
          <Field label="Broj paketa">
            <input type="number" value={packageCount} onChange={(e) => setPackageCount(e.target.value)} style={inputStyle} />
          </Field>
        </div>
      </Section>

      <Section title="Stavke">
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
            <thead>
              <tr>
                <th style={thStyle}>Naziv</th>
                <th style={{ ...thStyle, width: 100 }}>Šifra</th>
                <th style={{ ...thStyle, width: 90, textAlign: 'right' }}>Kol.</th>
                <th style={{ ...thStyle, width: 110 }}>JM</th>
                <th style={{ ...thStyle, width: 80, textAlign: 'center' }}>Opasno</th>
                <th style={{ ...thStyle, width: 90 }}>UN</th>
                <th style={{ ...thStyle, width: 90 }}>Klasa</th>
                <th style={{ ...thStyle, width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, idx) => (
                <tr key={idx}>
                  <td style={tdStyle}>
                    <input type="text" value={line.productName} onChange={(e) => updateLine(idx, 'productName', e.target.value)} style={cellInput} placeholder="Naziv" />
                  </td>
                  <td style={tdStyle}>
                    <input type="text" value={line.productCode} onChange={(e) => updateLine(idx, 'productCode', e.target.value)} style={cellInput} />
                  </td>
                  <td style={tdStyle}>
                    <input type="number" step="any" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)} style={{ ...cellInput, textAlign: 'right' }} />
                  </td>
                  <td style={tdStyle}>
                    <select value={line.unitCode} onChange={(e) => updateLine(idx, 'unitCode', e.target.value)} style={cellInput}>
                      {units.map((u) => <option key={u.value} value={u.value}>{u.value}</option>)}
                    </select>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <input type="checkbox" checked={line.isHazardous} onChange={(e) => updateLine(idx, 'isHazardous', e.target.checked)} />
                  </td>
                  <td style={tdStyle}>
                    <input type="text" value={line.unNumber} onChange={(e) => updateLine(idx, 'unNumber', e.target.value)} disabled={!line.isHazardous} style={cellInput} placeholder="4 cifre" />
                  </td>
                  <td style={tdStyle}>
                    <input type="text" value={line.hazardClass} onChange={(e) => updateLine(idx, 'hazardClass', e.target.value)} disabled={!line.isHazardous} style={cellInput} />
                  </td>
                  <td style={tdStyle}>
                    <button type="button" onClick={() => removeLine(idx)} style={{ ...iconBtn, color: '#fca5a5' }}>
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button type="button" onClick={() => setLines((prev) => [...prev, emptyLine()])} style={{ ...btnSecondary, marginTop: 10, alignSelf: 'flex-start' }}>
          <Plus size={14} /> Dodaj stavku
        </button>
      </Section>

      <div style={{ display: 'flex', gap: 10, marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--color-border)' }}>
        <button type="button" onClick={() => navigate('/eotpremnica')} style={btnSecondary}>Otkaži</button>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={handleSaveDraft} disabled={saving} style={btnSecondary}>
          <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj kao nacrt'}
        </button>
        <button type="button" onClick={handleSaveAndSubmit} disabled={saving} style={btnPrimary}>
          <Send size={14} /> {saving ? 'Slanje...' : 'Sačuvaj i pošalji'}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)', marginBottom: 16 }}>
      <h4 style={{ margin: 0, fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{title}</h4>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const selectStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const cellInput: React.CSSProperties = { padding: '6px 8px', border: '1px solid var(--color-border)', borderRadius: 4, fontSize: 12, width: '100%', background: 'var(--color-bg-surface)', color: 'var(--color-text)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--color-bg-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const thStyle: React.CSSProperties = { padding: '8px 6px', fontSize: 10, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const tdStyle: React.CSSProperties = { padding: '4px', borderBottom: '1px solid var(--color-border)' };
